const viteAppOrg = import.meta.env.VITE_APP_ORG as string;

import type { WritableComputedRef } from "vue";
import { ViteSSG } from "vite-ssg";
import App from "./App.vue";
import { createPinia } from "pinia";
import { vueUseToast } from "./plugins/useToast";
import { vueUseIntercom, initIntercom } from "./plugins/useIntercom";
import { vueUseClientSettings, fetchClientSettings } from "./plugins/useClientSettings";
import { configureRoutes } from "./router/routes-config";
import { getRoutes } from "./router/routes";
import type { RouteRecordRaw } from "vue-router";
import { setupAxiosInterceptors } from "./js/helpers/axios-interceptors";
import { createI18n, setLocale } from "./vue-i18n/vue-i18n";
import VueScrollTo from "vue-scrollto";
import "sweetalert2/dist/sweetalert2.min.css";
import { addSentry } from "./sentry-config";
import { useBaseContentStore } from "./stores/baseContentStore";

import "@sano-ui-library/src/assets/styles/main.scss";
import "@sano-ui-library/public/themes/participant.theme.css";
/*
   Org only CSS
   The import below is via javascript - its included here as we need the path to dynamic (incl. org name)
   Vite handles everything; imports, asset handling & injection into html
   This does mean the the css isn't available on pre-rendered pages before js can load it
   but we work around this for pre-rendering using `critters` in the vite.config to inline the 'critical css'
   This does create a console warning but it is a known issue - https://github.com/vitejs/vite/issues/12001
*/
if (!import.meta.env.SSR) {
   import(`./assets/org-css/${viteAppOrg}.org.css`);
}

// setup pinia initial state for SSR
function setPiniaInitialRenderState(pinia, initialState) {
   // The initial state comprises data that is serialized with your server-side generated HTML and is hydrated in the browser when accessed
   // https://github.com/antfu/vite-ssg#initial-state

   // in SSR mode, set the initial value for the pinia stores
   // NOTE: at this point pinia.state.value is an empty object. This object gets populated with stores by the time the build process completes.
   if (import.meta.env.SSR) {
      initialState.pinia = pinia.state.value;
   }
   // when this runs in the browser, use the initial state for pinia set at build, if there is any
   else {
      pinia.state.value = initialState.pinia || {};
   }
}

// SSG - setup routes for content rendering
function setRoutesContentRenderState(router, pinia) {
   // Initialise the base content pinia store with content data from the API.
   // At build, this runs one time for every pre-rendered page specified.
   router.beforeEach(async (to, from, next) => {
      // This is required for meta tag data to be pre-rendered
      const baseContentStore = useBaseContentStore();
      await baseContentStore.setContent();
      next();
   });
}

//  `export const createApp` is required instead of the original `createApp(App).mount('#app')`
export const createApp = ViteSSG(
   // the root component
   App,
   // vue-router options
   {
      routes: getRoutes() as Array<RouteRecordRaw>,
      // scroll to the top of the page on route change (https://router.vuejs.org/guide/advanced/scroll-behavior.html)
      // unless it's an anchor link
      scrollBehavior(to, from) {
         if (to.hash) {
            return {
               el: to.hash,
               top: 50,
               behavior: "smooth",
            };
         }
         if (to.params.savedPosition) return {};
         return { left: 0, top: 0 };
      },
   },
   // function to have custom setups
   async ({ app, router, routes, isClient, initialState }) => {
      // ❗ The order of execution below is important (pinia > SETUP > plugin installs)

      // modules
      // -----------------------------------------
      // sentry module
      addSentry(app, router);
      // i18n module
      const i18n = await createI18n();
      const { t: $t } = i18n.global;

      // pinia plugin and store setup
      // -----------------------------------------
      // 🔌 pinia store install
      const pinia = createPinia();
      app.use(pinia);

      // set the initial pre-render state for pinia
      setPiniaInitialRenderState(pinia, initialState);

      // Axios intercetors (requests and responses)
      // -----------------------------------------
      setupAxiosInterceptors(router, i18n.global);

      // 💫 SETUP
      // API requests required for the portal to run (run in parallel)
      // -----------------------------------------
      const clientSettings = await fetchClientSettings();

      if (!clientSettings) return;
      else if (!clientSettings.defaultLocale) {
         console.error("App runtime error - Default locale is missing from the client settings.");
         return;
      }

      // set locale and fallback locale to the default locale returned by the BE
      (i18n.global.fallbackLocale as WritableComputedRef<string>).value = clientSettings.defaultLocale;

      // set the locale to the default locale returned by the BE
      await setLocale(clientSettings.defaultLocale, i18n.global);

      // setup intercom state based on the Portal client settings
      const intercom = initIntercom(clientSettings.intercom);

      // external plugins
      // -----------------------------------------
      // 🔌 vue i18n plugin
      app.use(i18n);
      // 🔌 toast plugin
      app.use(vueUseToast, { $t });
      // 🔌 scroll to top plugin
      app.use(VueScrollTo);
      // 🔌 intercom plugin
      app.use(vueUseIntercom);

      // sano plugins
      // -----------------------------------------
      // 🔌 system features plugin
      app.use(vueUseClientSettings);

      // Router configuration (beforeEach etc.)
      // ------------------------------------
      configureRoutes(router, i18n.global, intercom, pinia, clientSettings);

      if (import.meta.env.SSR) {
         setRoutesContentRenderState(router, pinia);
      }
   }
);

// on hot-reload, clear the console (helps keep console clean for debugging)
// -----------------------------------------
if (import.meta.hot) {
   import.meta.hot.on("vite:beforeUpdate", () => console.clear());
}
